<template>
  <div class="tab-container">
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :key="repaintKey"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__select>
          <label
            v-if="table.items.length && table.items.some(item => item.supplier_invoice === null)"
            class="checkbox checkbox--table"
          >
            <input
              v-model="checked_all"
              type="checkbox"
              name="checked_all"
            />
            <span class="checkbox__checkmark"></span>
          </label>
        </template>
        <template #h__name><div class="table-default__left">Name</div></template>
        <template #select="{ row, index }">
          <label v-if="!row.supplier_invoice && (['approved', 'draft'].includes(requestData.status.code) && index !== table.items.length) || !['approved', 'draft'].includes(requestData.status.code)" class="checkbox checkbox--table">
            <input
              v-model="checked_items"
              type="checkbox"
              :value="row"
            />
            <span class="checkbox__checkmark"></span>
          </label>
        </template>
        <template #name="{ row, index }">
          <div class="table-default__left">
            <template v-if="['draft', 'approved'].includes(requestData.status.code)">
              <input
                v-if="index !== table.items.length && requestData.status.code === 'draft'"
                v-model="row.name"
                class="input input--200"
                @change="updateTableData(row.id, { name: row.name })"
                :tabindex="1"
              />
              <template v-else-if="index !== table.items.length && requestData.status.code === 'approved'">{{ row.name }}</template>
              <input
                v-else-if="index === table.items.length"
                @keydown.enter="onNewRowSubmit"
                v-model="new_row.name"
                class="input input--200"
                :tabindex="1"
                ref="new_item"
              />
            </template>
            <template v-else>{{ row.name }}</template>
          </div>
        </template>
        <template #expense="{ row, index }">
          <template v-if="['draft', 'approved'].includes(requestData.status.code)">
            <app-select
              v-if="index !== table.items.length && requestData.status.code === 'draft'"
              @input="updateTableData(row.id, { expense_item: row.expense_item })"
              v-model="row.expense_item"
              :options="expense_item_options"
              :reduce="item => item.id"
              :clearable="false"
              :calculate-position="withPopper"
              append-to-body
              label="name_expense"
              placeholder="Expense item"
              class="select--200 select--max-200"
              :tabindex="1"
            />
            <template v-else-if="index !== table.items.length && requestData.status.code === 'approved'">{{ row.expense_item.name_expense }}</template>
            <app-select
              v-else-if="index === table.items.length"
              @input="onExpenseChange"
              v-model="new_row.expense_item"
              :options="expense_item_options"
              :reduce="item => item.id"
              :clearable="false"
              :calculate-position="withPopper"
              append-to-body
              label="name_expense"
              placeholder="Expense item"
              class="select--200 select--max-200"
              :tabindex="1"
            />
          </template>
          <template v-else>{{ row.expense_item.name_expense }}</template>
        </template>
        <template #measurement="{ row, index }">
          <template v-if="['draft', 'approved'].includes(requestData.status.code)">
            <app-select
              v-if="index !== table.items.length && requestData.status.code === 'draft'"
              @input="updateTableData(row.id, { measurement: row.measurement })"
              :reduce="item => item.id"
              v-model="row.measurement"
              :options="measurements_options"
              :clearable="false"
              :calculate-position="withPopper"
              append-to-body
              placeholder="Measurement"
              class="select--200 select--max-200"
              :tabindex="1"
            />
            <template v-else-if="index !== table.items.length && requestData.status.code === 'approved'">{{ row.measurement.name }}</template>
            <app-select
              v-else-if="index === table.items.length"
              @input="onMeasurementChange"
              :reduce="item => item.id"
              v-model="new_row.measurement"
              :options="measurements_options"
              :clearable="false"
              :calculate-position="withPopper"
              append-to-body
              placeholder="Measurement"
              class="select--200 select--max-200"
              :tabindex="1"
              ref="measurement"
            />
          </template>
          <template v-else>{{ row.measurement.name }}</template>
        </template>
        <template #estimated_qty="{ row, index }">
          <template v-if="requestData.status.code === 'draft'">
            <app-fin-number
              v-if="index !== table.items.length"
              v-model="row.estimated_qty"
              class="input--150"
              @change.native="updateTableData(row.id, { estimated_qty: row.estimated_qty })"
              style="text-align: center"
              :tabindex="1"
            />
            <app-fin-number
              v-else
              @keydown.enter.native="onNewRowSubmit"
              v-model="new_row.estimated_qty"
              class="input--150"
              style="text-align: center"
              ref="new_qty"
              :tabindex="1"
            />
          </template>
          <template v-else>{{ row.estimated_qty || row.estimated_qty === 0 ? new Intl.NumberFormat('ru-RU').format(row.estimated_qty) : '' }}</template>
        </template>
        <template #estimated_price="{ row, index }">
          <template v-if="requestData.status.code === 'draft'">
            <app-fin-number
              v-if="index !== table.items.length"
              v-model="row.estimated_price"
              class="input--150"
              @change.native="updateTableData(row.id, { estimated_price: row.estimated_price })"
              style="text-align: center"
              :tabindex="1"
            />
            <app-fin-number
              v-else
              @keydown.enter.native="onNewRowSubmit"
              v-model="new_row.estimated_price"
              class="input--150"
              style="text-align: center"
              :tabindex="1"
            />
          </template>
          <template v-else>{{ row.estimated_price ? new Intl.NumberFormat('ru-RU').format(row.estimated_price) : '' }}</template>
        </template>
        <template #real_qty="{ row, index }">
          <template v-if="requestData.status.code === 'approved'">
            <app-fin-number
              v-if="index !== table.items.length"
              v-model="row.real_qty"
              class="input--150"
              @change.native="updateTableData(row.id, { real_qty: row.real_qty })"
              style="text-align: center"
              :tabindex="1"
            />
            <app-fin-number
              v-else
              v-model="new_row.real_qty"
              class="input--150"
              @keydown.enter.native="onNewRowSubmit"
              style="text-align: center"
              ref="new_real_qty"
              :tabindex="1"
            />
          </template>
          <template v-else>{{ row.real_qty || row.real_qty === 0 ? new Intl.NumberFormat('ru-RU').format(row.real_qty) : '' }}</template>
        </template>
        <template #real_price="{ row, index }">
          <template v-if="requestData.status.code === 'approved'">
            <app-fin-number
              v-if="index !== table.items.length"
              v-model="row.real_price"
              class="input--150"
              @change.native="updateTableData(row.id, { real_price: row.real_price })"
              style="text-align: center"
              :tabindex="1"
            />
            <app-fin-number
              v-else
              v-model="new_row.real_price"
              class="input--150"
              @keydown.enter.native="onNewRowSubmit"
              style="text-align: center"
              :tabindex="1"
            />
          </template>
          <template v-else>{{ row.real_price ? new Intl.NumberFormat('ru-RU').format(row.real_price) : '' }}</template>
        </template>
        <template #efd="{ row, index }">
          <label
            v-if="(['approved', 'draft'].includes(requestData.status.code) && index !== table.items.length) || !['approved', 'draft'].includes(requestData.status.code)"
            class="checkbox checkbox--table"
          >
            <input
              v-model="row.is_efd"
              type="checkbox"
              name="is_efd"
              @change="updateTableData(row.id, { is_efd: row.is_efd })"
            />
            <span class="checkbox__checkmark"></span>
          </label>
        </template>
        <template #amount_total="{ row }">
          {{ row.amount_total ? new Intl.NumberFormat('ru-RU').format(row.amount_total) : '' }}
        </template>
        <template #controls="{ row, index }">
          <div v-if="index !== table.items.length && requestData.status.code === 'draft'" class="table-default__controls">
            <button @click="onShowModalDelete(row.id)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="end">
        <span
          v-for="(data, index) in status_buttons[requestData.status.code]"
          :key="index"
        >
          <app-button
            v-if="!data.for_general || (data.for_general && is_general_manager)"
            @click.native="onShowModalUpdate(data)"
            :theme="data.modal_type === 'draft' ? 'warn' : data.modal_type === 'rejected' ? 'error' : 'default'"
          >
            {{ data.button_text }}
          </app-button>
        </span>
      </app-cells>
    </template>
    <modal
      name="delete-item-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideDeleteModal" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete item
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to do that?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onHideDeleteModal" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteRow" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="request-update"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('request-update')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Update request
        </div>
        <div class="popup__body">
          <div class="popup__subt">{{ modal_data.modal_text }}</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('request-update')" theme="dark">Cancel</app-button>
            <app-button @click.native="onUpdateRequestStatus(modal_data.modal_type)" ref="status_changed">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="invoice-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('invoice-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Request payment</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Due date" required>
              <app-date-picker
                v-model="form.due_date"
                placeholder="Date"
                :error="$v.form.due_date.$error"
              />
              <template #error>
                <div v-if="$v.form.due_date.$dirty && !$v.form.due_date.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Invoice attachment">
              <app-uploader
                v-model="form.attachment"
                :extensions="['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/zip', 'application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/bmp']"
                formDataFormats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
                class="custom-uploader--flex"
              />
            </app-form-group>
            <app-form-group label="Invoice number" label-for="invoice-number">
              <app-input
                v-model="form.invoice_number"
                id="invoice-number"
                name="invoice-number"
                placeholder="Invoice number"
              />
            </app-form-group>
            <app-form-group label="Supplier" required>
              <app-select
                @input="onSupplierChange"
                @search="onSupplierSearch"
                v-model="form.supplier"
                :options="supplier_options"
                :clearable="false"
                label="trade_name"
                placeholder="Supplier"
                :error="$v.form.supplier.$error"
              />
              <template #error>
                <div v-if="$v.form.supplier.$dirty && !$v.form.supplier.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Payment method" class="form-group--center" required v-if="payment_method_options.length > 1">
              <app-cells position="start" :indent="false">
                <app-radio
                  v-for="item in payment_method_options"
                  :key="item.code"
                  v-model="form.payment_method"
                  :value="item.code"
                  name="payment_method"
                  :checked="form.payment_method"
                  @change.native="onChangePaymentMethod"
                >
                  <template #label>{{ item.name }}</template>
                </app-radio>
              </app-cells>
            </app-form-group>
            <template v-if="form.payment_method === 'bank'">
              <app-form-group label="Bank account" required>
                <app-select
                  v-model="form.account"
                  :options="bank_account_options"
                  :clearable="false"
                  :searchable="false"
                  :display-field="option => `${option.account_number} (${option.bank?.short_name})`"
                  label="account_number"
                  placeholder="Bank account"
                  :error="$v.form.account.$error"
                />
                <template #error>
                  <div v-if="$v.form.account.$dirty && !$v.form.account.required">Required field</div>
                </template>
              </app-form-group>
            </template>
            <template v-if="form.payment_method === 'mobile'">
              <app-form-group label="Mobile account" required>
                <app-select
                  v-model="form.account"
                  :options="mobile_account_options"
                  :clearable="false"
                  :searchable="false"
                  :display-field="option => `${option.account_number} (${option.provider?.name})`"
                  label="account_number"
                  placeholder="Mobile account"
                  :error="$v.form.account.$error"
                />
                <template #error>
                  <div v-if="$v.form.account.$dirty && !$v.form.account.required">Required field</div>
                </template>
              </app-form-group>
            </template>
            <app-form-group v-if="form.payment_method === 'gepg'" label="Number" label-for="gepg_number" required>
              <app-input
                v-model="form.gepg_number"
                id="gepg_number"
                name="gepg_number"
                placeholder="Number"
                :error="$v.form.gepg_number.$error"
              />
              <template #error>
                <div v-if="$v.form.gepg_number.$dirty && !$v.form.gepg_number.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="invoice" :disabled="$v.form.$error">Request</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="transaction-modal"
      :width="800"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('transaction-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Add transaction</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckTransactionForm" class="arrange-popup">
            <app-grid>
              <template #item-1>
                <app-form-group label="Account" required>
                  <app-select
                    v-model="form_transaction.account"
                    :options="account_options"
                    :clearable="false"
                    placeholder="Account"
                    :error="$v.form_transaction.account.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_transaction.account.$dirty && !$v.form_transaction.account.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group label="Date" required>
                  <app-date-picker
                    v-model="form_transaction.date"
                    placeholder="Specify a date"
                    :error="$v.form_transaction.date.$error"
                  />
                  <template #error>
                    <div v-if="$v.form_transaction.date.$dirty && !$v.form_transaction.date.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
            </app-grid>
            <div class="arrange-popup__part">
              <div class="arrange-popup__block-title arrange-popup__block-title--fz13"><span>Items</span></div>
              <div class="transfer-sortable transfer-sortable--cashflow transfer-sortable--full">
                <div v-for="(element, index) in form_transaction.items" :key="`own_item_${index}`" class="transfer-sortable__item transfer-sortable__item--mb-20">
                  <div class="transfer-sortable__row">
                    <app-form-group label="Amount" :label-for="`amount-${index}`" required class="form-group--no-indent">
                      <app-fin-number
                        v-model="element.amount_total"
                        :id="`amount-${index}`"
                        placeholder="0"
                        name="amount"
                        readonly
                      />
                    </app-form-group>
                    <app-form-group label="Item" class="form-group--no-indent" required>
                      <app-select
                        v-model="element.expense_item"
                        :options="expense_item_options"
                        label="name_expense"
                        readonly
                        placeholder="Transaction item"
                      />
                    </app-form-group>
                    <app-form-group label="Supplier" class="form-group--no-indent">
                      <app-select
                        @search="onSupplierSearch"
                        v-model="element.supplier"
                        :options="supplier_options"
                        label="trade_name"
                        placeholder="Supplier"
                      />
                    </app-form-group>
                  </div>
                </div>
              </div>
            </div>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit_transaction" :disabled="$v.form_transaction.$error">Add</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <div class="flying-block" :class="{ 'flying-block--show': is_show_flying_block }">
      <div class="flying-block__text">Selected items: {{ checked_items.length }} ({{ checkedSum }} {{ requestData.currency.code }})</div>
<!--      <app-button @click.native="onShowInvoiceModal" size="small">Request payment</app-button>-->
      <app-button v-if="is_general_manager || is_accountant_supervisor" @click.native="onShowPaymentModal" size="small" theme="warn">Make payment</app-button>
    </div>
  </div>
</template>

<script>
import {
  deleteFinanceFundRequestsItems,
  getAdministrativeSupplierBankAccountSearch,
  getAdministrativeSupplierMobileAccountSearch,
  getAdministrativeSuppliers,
  getFinanceAccounts,
  getFinanceFundRequestsItems,
  getFinanceFundRequestsMeasurements,
  getFinancePaymentMethods,
  getFinanceTransactionsItemsList,
  patchFinanceFundRequests,
  patchFinanceFundRequestsItems,
  postFinanceFundRequestsCashflow,
  postFinanceFundRequestsItems,
  postFinanceFundRequestsSupplierInvoice,
} from '@/http'
import { createPopper } from '@popperjs/core'
import { required, requiredIf } from 'vuelidate/lib/validators'
import debounce from 'lodash/debounce'
import { format } from 'date-fns'

export default {
  name: 'FinanceFundRequestsDetailItems',
  props: ['requestData'],
  data() {
    return {
      page_loaded: false,
      product_to_delete: null,
      measurements_options: [],
      expense_item_options: [],
      table: {
        items: [],
        columns: [],
        options: {
          headings: {
            'select': '',
            'name': 'Name',
            'expense': 'Expense item',
            'measurement': 'Measurement',
            'estimated_qty': 'Est. qty',
            'estimated_price': 'Est. price',
            'real_qty': 'Real qty',
            'real_price': 'Real price',
            'efd': 'EFD',
            'amount_total': 'Total',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 1000,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_preloader: false,
      repaintKey: 1,
      new_row: {},
      placement: 'bottom',
      checked_all: false,
      checked_items: [],
      is_show_flying_block: false,
      status_buttons: {
        draft: [ { button_text: 'Make request', modal_type: 'requested', modal_text: 'Are you sure you want to set status Requested?', for_general: false } ],
        requested: [
          { button_text: 'Redraft', modal_type: 'draft', modal_text: 'Are you sure you want to set status Draft?', for_general: false },
          { button_text: 'Reject', modal_type: 'rejected', modal_text: 'Are you sure you want to set status Rejected?', for_general: true },
          { button_text: 'Approve', modal_type: 'approved', modal_text: 'Are you sure you want to set status Approved?', for_general: true },
        ],
        approved: [ { button_text: 'Complete', modal_type: 'completed', modal_text: 'Are you sure you want to complete request?', for_general: false } ],
      },
      columns_draft: [
        'name',
        'expense',
        'measurement',
        'estimated_qty',
        'estimated_price',
        'efd',
        'amount_total',
        'controls'
      ],
      columns_rejected: [
        'select',
        'name',
        'expense',
        'measurement',
        'estimated_qty',
        'estimated_price',
        'efd',
        'amount_total',
      ],
      columns_approved_completed: [
        'select',
        'name',
        'expense',
        'measurement',
        'estimated_qty',
        'estimated_price',
        'real_qty',
        'real_price',
        'efd',
        'amount_total',
      ],
      columns_requested: [
        'select',
        'name',
        'expense',
        'measurement',
        'estimated_qty',
        'estimated_price',
        'efd',
        'amount_total',
      ],
      modal_data: {},
      form: {},
      form_transaction: {},
      supplier_options: [],
      payment_method_options: [],
      bank_account_options: [],
      mobile_account_options: [],
      account_options: [],
    }
  },
  validations: {
    form: {
      due_date: { required },
      supplier: { required },
      payment_method: {
        required: requiredIf(function() {
          return this.payment_method_options.length > 1
        })
      },
      gepg_number: {
        required: requiredIf(function() {
          return this.form.payment_method === 'gepg'
        })
      },
      account: {
        required: requiredIf(function() {
          return ['bank', 'mobile'].includes(this.form.payment_method)
        })
      },
    },
    form_transaction: {
      account: { required },
      date: { required },
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_accountant_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('accountant_supervisor')
    },
    checkedSum() {
      return this.checked_items
        .map(item => Number(item.amount_total))
        .reduce((a, b) => {
          return a + b
        }, 0)
        .toLocaleString()
    },
  },
  watch: {
    requestData(newVal, oldVal) {
      if (newVal.status.code !== oldVal.status.code) {
        this.changeTableColumns(this.requestData.status.code, false)
      }
    },
    checked_all(value) {
      if (value) {
        this.table.items.forEach(item => {
          if (!this.checked_items.map(({ id }) => id).includes(item.id) && item.supplier_invoice === null) this.checked_items.push(item)
        })
      } else {
        this.checked_items = []
      }
    },
    checked_items(value) {
      this.is_show_flying_block = value.length > 0
      if (!this.checked_items.length) this.checked_all = false
    },
  },
  created() {
    getFinanceFundRequestsItems(Number(this.$route.params.id))
      .then(response => {
        this.table.items = response.data
        this.changeTableColumns(this.requestData.status.code, true)
        this.page_loaded = true
      })
    getFinanceFundRequestsMeasurements().then(response => { this.measurements_options = response.data })
    getFinanceTransactionsItemsList({ business_unit: this.requestData.business_unit.id, expense: true })
      .then(response => {
        this.expense_item_options = response.data
      })
    getFinanceAccounts({ outcome: true }).then(response => { this.account_options = response.data })
  },
  methods: {
    onNewRowSubmit() {
      const data = {}
      if (this.requestData.status.code === 'draft') {
        if (this.new_row.name && this.new_row.expense_item && this.new_row.measurement && this.new_row.estimated_qty && this.new_row.estimated_price) {
          data.name = this.new_row.name
          data.expense_item = this.new_row.expense_item
          data.measurement = this.new_row.measurement
          data.estimated_qty = this.new_row.estimated_qty
          data.estimated_price = this.new_row.estimated_price
          postFinanceFundRequestsItems(Number(this.$route.params.id), data)
            .then(response => {
              this.$emit('changeStatus')
              this.table.items = response.data
              this.table.items.push({})
              this.new_row = {}
              this.$nextTick(() => {
                this.$refs.new_item.focus()
              })
              this.repaintKey += 1
              this.$notify({
                type: 'success',
                title: 'Item added',
              })
            })
        } else {
          this.$notify({
            type: 'info',
            title: 'Check that the table row fields are filled in correctly',
          })
        }
      } else {
        if (this.new_row.name && this.new_row.expense_item && this.new_row.measurement && this.new_row.real_qty && this.new_row.real_price) {
          data.name = this.new_row.name
          data.expense_item = this.new_row.expense_item
          data.measurement = this.new_row.measurement
          data.real_qty = this.new_row.real_qty
          data.real_price = this.new_row.real_price
          postFinanceFundRequestsItems(Number(this.$route.params.id), data)
            .then(response => {
              this.$emit('changeStatus')
              this.table.items = response.data
              this.table.items.push({})
              this.new_row = {}
              this.$nextTick(() => {
                this.$refs.new_item.focus()
              })
              this.repaintKey += 1
              this.$notify({
                type: 'success',
                title: 'Item added',
              })
            })
        } else {
          this.$notify({
            type: 'info',
            title: 'Check that the table row fields are filled in correctly',
          })
        }
      }
    },
    updateTableData(item_id, data) {
      this.table_preloader = true
      patchFinanceFundRequestsItems(Number(this.$route.params.id), item_id, data)
        .then(response => {
          this.$emit('changeStatus')
          this.table.items = response.data
          if (['draft', 'approved'].includes(this.requestData.status.code)) this.table.items.push({})
          this.repaintKey += 1
          this.$notify({
            type: 'success',
            title: 'Item changed',
          })
          this.table_preloader = false
        })
    },
    changeTableColumns(status, created) {
      if (status === 'draft') {
        this.table.columns = this.columns_draft
        this.table.items.push({})
      } else if (status === 'rejected') {
        this.table.columns = this.columns_rejected
      } else if (status === 'requested') {
        this.table.columns = this.columns_requested
        if (!created) this.table.items.pop()
      } else {
        this.table.columns = this.columns_approved_completed
        if (status === 'approved') this.table.items.push({})
      }
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.invoice.preload = true
      const data = {
        due_date: this.form.due_date,
        invoice_number: this.form.invoice_number,
        supplier: this.form.supplier.id,
        payment_method: this.form.payment_method,
        fund_request_items: this.checked_items.map(item => item.id),
      }
      if (this.form.account) data.account = this.form.account.id
      if (this.form.attachment) data.attachment = this.form.attachment.id
      if (this.form.payment_method === 'gepg') data.gepg_number = this.form.gepg_number
      postFinanceFundRequestsSupplierInvoice(Number(this.$route.params.id), data)
        .then(() => {
          getFinanceFundRequestsItems(Number(this.$route.params.id))
            .then(response => {
              this.table.items = response.data
              if (this.requestData.status.code === 'approved') this.table.items.push({})
            })
          this.$notify({
            type: 'success',
            title: 'Invoice created',
          })
          this.$refs.invoice.preload = false
          this.is_show_flying_block = false
          this.checked_items = []
          this.$modal.hide('invoice-modal')
        })
        .catch(() => { this.$refs.invoice.preload = false })
    },
    onCheckTransactionForm() {
      this.$v.form_transaction.$touch()
      if (this.$v.form_transaction.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendFormTransaction()
      }
    },
    sendFormTransaction() {
      this.$refs.submit_transaction.preload = true
      const formData = { ...this.form_transaction }
      formData.account = formData.account.id
      formData.items = formData.items.map(({amount_total, expense_item, supplier}) => {
        const data = {
          amount: amount_total,
          item: expense_item.id,
        }
        if (supplier) data.supplier = supplier.id
        return data
      })
      postFinanceFundRequestsCashflow(Number(this.$route.params.id), formData)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Transaction created',
          })
          this.$refs.submit_transaction.preload = false
          this.is_show_flying_block = false
          this.checked_items = []
          this.$modal.hide('transaction-modal')
        })
        .catch(() => { this.$refs.submit_transaction.preload = false })
    },
    onSupplierChange(e) {
      this.form.payment_method = null
      getFinancePaymentMethods({ for_supplier: e.id }).then(res => {
        if (res.data.length === 1) this.form.payment_method = res.data[0].code
        this.payment_method_options = res.data
      })
    },
    onChangePaymentMethod() {
      this.form.account = null
      if (this.form.payment_method === 'bank') {
        getAdministrativeSupplierBankAccountSearch(this.form.supplier.id)
          .then(response => { this.bank_account_options = response.data })
        return
      }
      if (this.form.payment_method === 'mobile') {
        getAdministrativeSupplierMobileAccountSearch(this.form.supplier.id)
          .then(response => { this.mobile_account_options = response.data })
      }
    },
    onShowInvoiceModal() {
      this.form = {
        account: null,
        payment_method: null,
      }
      this.$v.form.$reset()
      this.$modal.show('invoice-modal')
    },
    onShowPaymentModal() {
      this.form_transaction = {
        items: this.checked_items,
        date: format(new Date(), 'yyyy-MM-dd'),
      }
      this.$v.form_transaction.$reset()
      this.$modal.show('transaction-modal')
    },
    onShowModalDelete(id) {
      this.product_to_delete = id
      this.$modal.show('delete-item-modal')
    },
    onHideDeleteModal() {
      this.product_to_delete = null
      this.$modal.hide('delete-item-modal')
    },
    onDeleteRow() {
      this.$refs.delete.preload = true
      deleteFinanceFundRequestsItems(Number(this.$route.params.id), this.product_to_delete)
        .then(response => {
          this.$emit('changeStatus')
          this.table.items = response.data
          this.table.items.push({})
          this.new_row = {}
          this.repaintKey += 1
          this.$notify({
            type: 'success',
            title: 'Item deleted',
          })
          this.onHideDeleteModal()
        })
        .catch(() => { this.$refs.delete.preload = false })
    },
    onExpenseChange() {
      this.$nextTick(() => {
        this.$refs.measurement.$el.querySelector('input').focus()
      })
    },
    onMeasurementChange() {
      this.$nextTick(() => {
        if (this.requestData.status.code === 'draft') this.$refs.new_qty.$el.focus()
        else this.$refs.new_real_qty.$el.focus()
      })
    },
    onShowModalUpdate(data) {
      if (this.new_row.name) {
        this.$notify({
          type: 'info',
          title: 'Complete adding the item',
        })
        return
      }
      this.modal_data = data
      this.$modal.show('request-update')
    },
    onUpdateRequestStatus(status) {
      this.$refs.status_changed.preload = true
      patchFinanceFundRequests(Number(this.$route.params.id), { status })
        .then(() => {
          this.$emit('changeStatus')
          this.$refs.status_changed.preload = false
          this.$modal.hide('request-update')
        })
        .catch(() => { this.$refs.status_changed.preload = false })
    },
    withPopper(dropdownList, component, {width}) {
      dropdownList.style.width = width
      createPopper(component.$refs.toggle, dropdownList, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 0],
              adaptive: true
            }
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn({state}) {
              component.$el.classList.toggle('drop-up', state.placement === 'top')
            },
          },
        ]
      })
    },
    onSupplierSearch(search, loading) {
      loading(true)
      this.searchSupplier(loading, search, this)
    },
    searchSupplier: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.supplier_options = []
      } else {
        getAdministrativeSuppliers({search}).then(response => {
          vm.supplier_options = response.data
          loading(false)
        })
      }
    }, 350),
  }
}
</script>
